import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import Layout from '@components/layout/Layout';
import { SUBSCRIPTION_PLANS } from '@utils/constants';
import { useAuth } from '@context/AuthContext';
import { subscriptionAPI } from '@services/photographerApi';

type BillingPeriod = 'monthly' | 'annual';
type PlanKey = 'basic' | 'pro' | 'enterprise';
type FeatureKey = Extract<keyof typeof SUBSCRIPTION_PLANS.basic.features, string>;

const PLAN_KEYS: PlanKey[] = ['basic', 'pro', 'enterprise'];

const FEATURE_KEYS: FeatureKey[] = [
  'events',
  'photos',
  'storage',
  'bandwidth',
  'analytics',
  'apiAccess',
  'whiteLabel',
  'support',
];

const FEATURE_LABELS: Record<FeatureKey, string> = {
  events: 'Events',
  photos: 'Photos per event',
  storage: 'Storage',
  bandwidth: 'Bandwidth',
  customDomain: 'Custom domain',
  analytics: 'Analytics',
  apiAccess: 'API access',
  whiteLabel: 'White label',
  support: 'Support',
};

type FeatureValue = number | boolean | string;

function formatFeatureValue(key: FeatureKey, value: FeatureValue): string | boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value;
  if (key === 'events' || key === 'photos') {
    return value === -1 ? 'Unlimited' : value.toLocaleString();
  }
  return String(value);
}

function formatPrice(
  plan: (typeof SUBSCRIPTION_PLANS)[PlanKey],
  billing: BillingPeriod
): string {
  if (billing === 'monthly') return `$${plan.monthlyPrice.toFixed(2)}`;
  const perMonth = plan.annualPrice / 12;
  return `$${perMonth.toFixed(2)}`;
}

function annualSavings(plan: (typeof SUBSCRIPTION_PLANS)[PlanKey]): number {
  const annualIfMonthly = plan.monthlyPrice * 12;
  return Math.round(
    ((annualIfMonthly - plan.annualPrice) / annualIfMonthly) * 100
  );
}

const PricingPage: React.FC = () => {
  const [billing, setBilling] = useState<BillingPeriod>('monthly');
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  const handleSelect = async (planId: PlanKey): Promise<void> => {
    if (!isAuthenticated || !user?.id) {
      navigate('/register');
      return;
    }
    const interval = billing === 'annual' ? 'year' : 'month';
    const origin = window.location.origin;
    const res = await subscriptionAPI.createCheckout(user.id, {
      plan: planId,
      interval,
      success_url: `${origin}/dashboard/billing?checkout=success`,
      cancel_url: `${origin}/dashboard/billing?checkout=cancel`,
    });
    if (res.url) window.location.href = res.url;
  };

  return (
    <Layout>
      <div className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Simple, transparent pricing
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Choose the plan that fits your photography business. Subscribe to
            get started &mdash; no free trial required.
          </p>

          {/* Billing toggle */}
          <div className="mt-8 inline-flex items-center bg-gray-100 rounded-full p-1">
            <button
              onClick={() => setBilling('monthly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                billing === 'monthly'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBilling('annual')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                billing === 'annual'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Annual
              <span className="ml-2 text-xs text-green-600 font-semibold">
                Save up to 17%
              </span>
            </button>
          </div>
        </div>

        {/* Plan cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {PLAN_KEYS.map((planKey) => {
            const plan = SUBSCRIPTION_PLANS[planKey];
            const savings = annualSavings(plan);
            const isHighlighted = plan.highlighted;

            return (
              <div
                key={planKey}
                className={`relative bg-white rounded-lg shadow-md flex flex-col ${
                  isHighlighted
                    ? 'border-2 border-blue-500 shadow-lg'
                    : 'border border-gray-200'
                }`}
              >
                {isHighlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="p-6 flex-1">
                  <h2 className="text-xl font-bold text-gray-900 mb-1">
                    {plan.name}
                  </h2>

                  <div className="mt-4 mb-2">
                    <span className="text-4xl font-extrabold text-gray-900">
                      {formatPrice(plan, billing)}
                    </span>
                    <span className="text-gray-500 text-sm ml-1">/mo</span>
                  </div>

                  <div className="text-sm text-gray-500 mb-6 min-h-[20px]">
                    {billing === 'annual' ? (
                      <>
                        ${plan.annualPrice.toFixed(2)} billed annually
                        {savings > 0 && (
                          <span className="ml-1 text-green-600 font-medium">
                            ({savings}% off)
                          </span>
                        )}
                      </>
                    ) : (
                      'Billed monthly'
                    )}
                  </div>

                  <ul className="space-y-3">
                    {FEATURE_KEYS.map((featureKey) => {
                      const value = plan.features[featureKey] as FeatureValue;
                      const formatted = formatFeatureValue(featureKey, value);
                      const isBool = typeof formatted === 'boolean';

                      return (
                        <li
                          key={featureKey}
                          className="flex items-start gap-2 text-sm"
                        >
                          {isBool ? (
                            formatted ? (
                              <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            ) : (
                              <X className="w-4 h-4 text-gray-300 mt-0.5 flex-shrink-0" />
                            )
                          ) : (
                            <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          )}
                          <span
                            className={
                              isBool && !formatted
                                ? 'text-gray-400'
                                : 'text-gray-700'
                            }
                          >
                            {FEATURE_LABELS[featureKey]}
                            {!isBool && `: ${formatted}`}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="p-6 pt-0">
                  <button
                    onClick={() => handleSelect(plan.planId)}
                    className={`w-full py-3 px-4 rounded-lg font-semibold text-sm transition-colors ${
                      isHighlighted
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    {plan.cta}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Feature comparison table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900">
              Feature comparison
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600 w-1/3">
                    Feature
                  </th>
                  {PLAN_KEYS.map((planKey) => (
                    <th
                      key={planKey}
                      className={`text-center px-4 py-4 text-sm font-semibold ${
                        SUBSCRIPTION_PLANS[planKey].highlighted
                          ? 'text-blue-600'
                          : 'text-gray-600'
                      }`}
                    >
                      {SUBSCRIPTION_PLANS[planKey].name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {FEATURE_KEYS.map((featureKey, idx) => (
                  <tr
                    key={featureKey}
                    className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}
                  >
                    <td className="px-6 py-4 text-sm font-medium text-gray-700">
                      {FEATURE_LABELS[featureKey]}
                    </td>
                    {PLAN_KEYS.map((planKey) => {
                      const value = SUBSCRIPTION_PLANS[planKey].features[
                        featureKey
                      ] as FeatureValue;
                      const formatted = formatFeatureValue(featureKey, value);
                      const isBool = typeof formatted === 'boolean';

                      return (
                        <td
                          key={planKey}
                          className="px-4 py-4 text-center text-sm text-gray-700"
                        >
                          {isBool ? (
                            formatted ? (
                              <Check className="w-5 h-5 text-green-500 mx-auto" />
                            ) : (
                              <X className="w-5 h-5 text-gray-300 mx-auto" />
                            )
                          ) : (
                            <span>{formatted as string}</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer CTA */}
        <div className="mt-16 text-center">
          <p className="text-gray-600 mb-4">
            Questions about pricing or need a custom plan?
          </p>
          <a
            href="mailto:sales@pixel-share.com"
            className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
          >
            Contact our sales team
          </a>
        </div>
      </div>
    </Layout>
  );
};

export default PricingPage;
